import { RawLineChartDataPoint } from "types";
import { toFixed } from "@shared/utils/numbers";
import {
  ConsolidatedAccountData,
  LineChartDataPointWithExchange,
} from "@shared/types";
import { TIME_RANGE_1_DAY } from "common/constants";

export const getFirstLastInGraphData = (
  graphData: RawLineChartDataPoint[] | LineChartDataPointWithExchange[],
  index: number
) => {
  const first = Number(graphData[0]?.[index]);
  const last = Number(graphData[graphData.length - 1]?.[index]);
  return [first, last];
};

export const getPriceChangePercentage = (first: number, last: number) => {
  const change = toFixed(((last - first) / first) * 100, 2);
  if (Math.abs(change) === Infinity || isNaN(change)) return undefined;
  return change;
};

export const getUsdValueWithPrecision = (value: number) => {
  if (value > 100000) return toFixed(value, 0);
  else if (value > 10000) return toFixed(value, 1);
  return value;
};

/** The accounts with each client uuid once (the first of its entries). */
export const getUniqueAccounts = <T extends { client_uuid: string }>(
  accountsData: T[]
) => {
  const processedAccountsUUID = new Set<string>([]);
  return accountsData.filter((account) => {
    if (processedAccountsUUID.has(account.client_uuid)) return false;
    processedAccountsUUID.add(account.client_uuid);
    return true;
  });
};

/**
 * The history with the live total as its point for now, in place of any
 * snapshot at or after now. A history with no snapshots stays empty: a
 * single live point is not a history.
 */
export const withLivePoint = (
  history: RawLineChartDataPoint[],
  usdTotal: number,
  btcTotal: number,
  now = Math.floor(Date.now() / 1000)
): RawLineChartDataPoint[] => {
  const before = history.filter((point) => point[0] < now);
  if (!before.length) return history;
  return [...before, [now, usdTotal, btcTotal]];
};

/** Sums the accounts' balance and its change over the last 24h, counting each account once. */
export const getAccountsTotals = (
  accountsData: ConsolidatedAccountData[],
  isUsd: boolean
) => {
  let total = 0;
  let last24Change = 0;
  getUniqueAccounts(accountsData).forEach((account) => {
    total += (isUsd ? account.total_in_usd : account.total_in_btc) || 0;
    const graphData = account.graph_data?.[TIME_RANGE_1_DAY] || [];
    const [first, last] = getFirstLastInGraphData(graphData, isUsd ? 1 : 2);
    if (graphData.length) last24Change += last - first;
  });
  return { total, last24Change };
};
